/**
 * User Context - Connected to Backend API
 * Provides user data from NextAuth session and geolocation
 * Includes real-time credit balance updates via polling and events
 */
"use client";

import { createContext, useContext, ReactNode, useState, useEffect, useCallback, useRef } from "react";
import { useSession } from "next-auth/react";
import { getCreditBalance, isApiSuccess } from "@/shared/lib/api";
import { captureError, updateContext as updateObservabilityContext } from "@/shared/lib/observability";

// Credit refresh interval (30 seconds)
const CREDIT_POLL_INTERVAL = 30 * 1000;

// Custom event name for triggering immediate credit refresh
export const CREDIT_CHANGE_EVENT = "hoku:creditChange";

// Types
export interface User {
  id: string;
  name: string | null;
  email: string;
  image?: string | null;
  emailVerified?: boolean;
  credit_balance?: number;
  creditBalance?: number;
  dress_upload_count?: number;
  gender?: string | null;
  date_of_birth?: string | null;
  has_onboarded?: boolean;
  hasOnboarded?: boolean;
  coins?: number;
  role?: "USER" | "ADMIN" | "DEMO";
  onboardingStep?: number;
  onboardingComplete?: boolean;
  aiFeatureUseCount?: number;
  hasSeenPodsTutorial?: boolean;
  prefsCapturedAt?: string | null;
}

export interface LocationState {
  coords: { latitude: number; longitude: number } | null;
  city: string | null;
  state: string | null;
  country: string | null;
  error: string | null;
  loading: boolean;
}

// Default empty location state
const DEFAULT_LOCATION: LocationState = {
  coords: null,
  city: null,
  state: null,
  country: null,
  error: null,
  loading: true,
};

// Context type
interface UserContextType {
  user: User | null;
  userId: string | null;
  location: LocationState;
  creditBalance: number;
  creditBalanceLoading: boolean; // True until we've fetched real balance from API
  userGender: string | null;
  userDateOfBirth: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  updateSession: (data: Partial<User>) => Promise<void>;
  refreshLocation: () => void;
  refreshUser: () => Promise<User | null>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}

export function UserProvider({ children }: { children: ReactNode }) {
  const { data: session, status, update } = useSession();
  const [location, setLocation] = useState<LocationState>(DEFAULT_LOCATION);
  // Local state for credit balance - fetched from API (not session)
  const [localBalance, setLocalBalance] = useState<number | null>(null);
  // Track if we've fetched the real balance from API
  const [balanceFetched, setBalanceFetched] = useState(false);

  // Mirror the authenticated userId into the observability context so every
  // log line and PostHog event auto-includes it (and the proxy can propagate
  // it as the trace key to the backend).
  useEffect(() => {
    if (session?.user?.id) {
      updateObservabilityContext({ userId: session.user.id });
    }
  }, [session?.user?.id]);

  // Fetch location on mount
  const fetchLocation = async () => {
    setLocation(prev => ({ ...prev, loading: true, error: null }));

    // Check if geolocation is available
    if (!navigator.geolocation) {
      setLocation(prev => ({
        ...prev,
        loading: false,
        error: "Geolocation is not supported by your browser",
      }));
      return;
    }

    // Get current position
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        // Try to reverse geocode to get city/state/country
        try {
          // Using a free reverse geocoding API
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10`
          );

          if (response.ok) {
            const data = await response.json();
            const address = data.address || {};

            setLocation({
              coords: { latitude, longitude },
              city: address.city || address.town || address.village || null,
              state: address.state || null,
              country: address.country || null,
              error: null,
              loading: false,
            });
          } else {
            // Fallback: just use coordinates without city info
            setLocation({
              coords: { latitude, longitude },
              city: null,
              state: null,
              country: null,
              error: null,
              loading: false,
            });
          }
        } catch {
          // Fallback: just use coordinates without city info
          setLocation({
            coords: { latitude, longitude },
            city: null,
            state: null,
            country: null,
            error: null,
            loading: false,
          });
        }
      },
      (error) => {
        let errorMessage = "Unable to retrieve location";
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = "Location permission denied";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location information unavailable";
            break;
          case error.TIMEOUT:
            errorMessage = "Location request timed out";
            break;
        }
        setLocation({
          coords: null,
          city: null,
          state: null,
          country: null,
          error: errorMessage,
          loading: false,
        });
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000, // Cache for 5 minutes
      }
    );
  };

  // NOTE: no on-mount geolocation fetch. Nothing in the app reads
  // UserContext.location (weather has its own geolocation in use-enhanced-weather),
  // so firing navigator.geolocation.getCurrentPosition — a permission PROMPT plus
  // a cross-origin reverse-geocode — on every page load was pure dead weight.
  // `refreshLocation` stays exposed for any future consumer that actually needs it.

  // Build user object from session - null if not authenticated
  // ONLY use localBalance from API - never use stale session value
  // Show 0 while loading to avoid showing old cached session value
  const currentCoins = localBalance ?? 0;
  const creditBalanceLoading = !balanceFetched && status === "authenticated";

  const user: User | null = session?.user
    ? {
        id: session.user.id,
        name: session.user.name,
        email: session.user.email!,
        image: session.user.image,
        emailVerified: session.user.emailVerified,
        hasOnboarded: session.user.hasOnboarded,
        has_onboarded: session.user.hasOnboarded,
        coins: currentCoins,
        credit_balance: currentCoins,
        creditBalance: currentCoins,
        role: session.user.role,
        onboardingStep: session.user.onboardingStep ?? 0,
        onboardingComplete: session.user.onboardingComplete ?? false,
        aiFeatureUseCount: session.user.aiFeatureUseCount ?? 0,
        gender: session.user.gender ?? null,
        prefsCapturedAt: session.user.prefsCapturedAt ?? null,
      }
    : null;

  const updateSession = async (data: Partial<User>) => {
    await update(data);
  };

  /**
   * Refresh user data from the backend API
   * Fetches the latest credit balance and updates local state
   * Returns the updated user object
   */
  const refreshUser = useCallback(async (): Promise<User | null> => {
    if (!session?.user?.id) {
      return null;
    }

    try {
      const response = await getCreditBalance();
      if (isApiSuccess(response)) {
        const newBalance = response.data.balance;
        setLocalBalance(newBalance);
        setBalanceFetched(true); // Mark that we've fetched the real balance

        // Return updated user object with new balance
        return {
          id: session.user.id,
          name: session.user.name,
          email: session.user.email!,
          image: session.user.image,
          emailVerified: session.user.emailVerified,
          hasOnboarded: session.user.hasOnboarded,
          has_onboarded: session.user.hasOnboarded,
          coins: newBalance,
          credit_balance: newBalance,
          creditBalance: newBalance,
          role: session.user.role,
          onboardingStep: session.user.onboardingStep ?? 0,
          onboardingComplete: session.user.onboardingComplete ?? false,
          aiFeatureUseCount: session.user.aiFeatureUseCount ?? 0,
          prefsCapturedAt: session.user.prefsCapturedAt ?? null,
        };
      }
    } catch (error) {
      captureError(error, { feature: "shared:UserContext", op: "Failed to refresh user balance" });
      // Even on error, mark as fetched so we don't show loading forever
      setBalanceFetched(true);
    }

    return user;
  }, [session?.user, user]);

  // Polling interval ref to avoid stale closures
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Set up polling for credit balance updates
  useEffect(() => {
    // Only poll if user is authenticated
    if (status !== "authenticated" || !session?.user?.id) {
      return;
    }

    // Initial fetch on mount
    refreshUser();

    // Set up polling interval
    pollIntervalRef.current = setInterval(() => {
      refreshUser();
    }, CREDIT_POLL_INTERVAL);

    // Cleanup on unmount or when auth status changes
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [status, session?.user?.id]); // Don't include refreshUser to avoid infinite loop

  // Listen for custom credit change events (triggered after AI operations)
  useEffect(() => {
    const handleCreditChange = () => {
      // Immediate refresh when credit change event is dispatched
      refreshUser();
    };

    // Add event listener
    window.addEventListener(CREDIT_CHANGE_EVENT, handleCreditChange);

    // Cleanup
    return () => {
      window.removeEventListener(CREDIT_CHANGE_EVENT, handleCreditChange);
    };
  }, [refreshUser]);

  const value: UserContextType = {
    user,
    userId: user?.id || null,
    location,
    creditBalance: currentCoins,
    creditBalanceLoading,
    userGender: user?.gender ?? null,
    userDateOfBirth: user?.date_of_birth ?? null,
    isLoading: status === "loading",
    isAuthenticated: status === "authenticated",
    updateSession,
    refreshLocation: fetchLocation,
    refreshUser,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

/**
 * Utility function to trigger immediate credit balance refresh
 * Call this after any AI operation that deducts credits
 */
export function triggerCreditRefresh(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(CREDIT_CHANGE_EVENT));
  }
}
